/**
 * Generates a URL for a skill page in a program context
 *
 * @param {string} skillId - The unique identifier for the skill
 * @param {string} programSlug - The URL-friendly identifier for the program
 * @returns {string} The complete URL path to the skill page
 *
 * @example
 * ```typescript
 * const url = generateSkillsPageUrl('skill123', 'data-science');
 * // Returns: '/programs/data-science/skills/skill123'
 * ```
 */
export const generateSkillsPageUrl = (skillId: string, programSlug: string): string => {
  const encodedSkillId = encodeURIComponent(skillId);
  return `/programs/${programSlug}/skills/${encodedSkillId}`;
};

/**
 * Generates a URL for a skill page within a specific skillset context
 *
 * @param {string} skillId - The unique identifier for the skill
 * @param {string} programSlug - The URL-friendly identifier for the program
 * @param {string} skillsetSlug - The URL-friendly identifier for the skillset
 * @returns {string} The complete URL path to the skill page within the skillset
 *
 * @example
 * ```typescript
 * const url = generateSkillsetSkillsPageUrl('skill123', 'data-science', 'machine-learning');
 * // Returns: '/programs/data-science/skillsets/machine-learning/skill/skill123'
 * ```
 */
export const generateSkillsetSkillsPageUrl = (skillId: string, programSlug: string, skillsetSlug: string): string => {
  const encodedSkillId = encodeURIComponent(skillId);
  return `/programs/${programSlug}/skillsets/${skillsetSlug}/skill/${encodedSkillId}`;
};
